import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, AlertTriangle, Package } from 'lucide-react';
import { format } from 'date-fns';
import { reagentService, reagentUsageService } from '../../lib/services.labOps';

const ReagentInventory: React.FC = () => {
  const [reagents, setReagents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReagent, setEditingReagent] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    lot_no: '',
    quantity: 0,
    unit: 'ml',
    reorder_level: 0,
    expiry: '',
  });
  const [selectedTab, setSelectedTab] = useState<'all' | 'low_stock' | 'expiring'>('all');
  const [usageHistory, setUsageHistory] = useState<any[]>([]);
  const [showUsageModal, setShowUsageModal] = useState(false);

  useEffect(() => {
    loadReagents();
  }, [selectedTab]);

  const loadReagents = async () => {
    setLoading(true);
    try {
      let data;
      if (selectedTab === 'low_stock') {
        data = await reagentService.getLowStock();
      } else if (selectedTab === 'expiring') {
        data = await reagentService.getExpiringSoon(30);
      } else {
        data = await reagentService.getAll();
      }
      setReagents(data);
    } catch (error) {
      console.error('Error loading reagents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingReagent) {
        await reagentService.update(editingReagent.id, formData);
      } else {
        await reagentService.create(formData);
      }
      setShowModal(false);
      setEditingReagent(null);
      setFormData({
        name: '',
        lot_no: '',
        quantity: 0,
        unit: 'ml',
        reorder_level: 0,
        expiry: '',
      });
      loadReagents();
    } catch (error) {
      console.error('Error saving reagent:', error);
      alert('Failed to save reagent');
    }
  };

  const handleEdit = (reagent: any) => {
    setEditingReagent(reagent);
    setFormData({
      name: reagent.name,
      lot_no: reagent.lot_no || '',
      quantity: reagent.quantity,
      unit: reagent.unit,
      reorder_level: reagent.reorder_level,
      expiry: reagent.expiry || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this reagent?')) return;
    
    try {
      await reagentService.delete(id);
      loadReagents();
    } catch (error) {
      console.error('Error deleting reagent:', error);
      alert('Failed to delete reagent');
    }
  };

  const viewUsageHistory = async (reagent: any) => {
    try {
      const usage = await reagentUsageService.getByReagent(reagent.id);
      setUsageHistory(usage);
      setShowUsageModal(true);
    } catch (error) {
      console.error('Error loading usage history:', error);
    }
  };

  const isExpiringSoon = (expiry: string) => {
    if (!expiry) return false;
    const expiryDate = new Date(expiry);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiryDate <= thirtyDaysFromNow;
  };

  const isLowStock = (quantity: number, reorderLevel: number) => {
    return quantity <= reorderLevel;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reagent Inventory</h1>
          <p className="text-gray-600">Manage laboratory reagents and consumables</p>
        </div>
        <button
          onClick={() => {
            setEditingReagent(null);
            setFormData({
              name: '',
              lot_no: '',
              quantity: 0,
              unit: 'ml',
              reorder_level: 0,
              expiry: '',
            });
            setShowModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          <span>Add Reagent</span>
        </button>
      </div>

      <div className="mb-6 flex space-x-4">
        <button
          onClick={() => setSelectedTab('all')}
          className={`px-4 py-2 rounded-md ${
            selectedTab === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Reagents
        </button>
        <button
          onClick={() => setSelectedTab('low_stock')}
          className={`px-4 py-2 rounded-md ${
            selectedTab === 'low_stock'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Low Stock
        </button>
        <button
          onClick={() => setSelectedTab('expiring')}
          className={`px-4 py-2 rounded-md ${
            selectedTab === 'expiring'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Expiring Soon
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lot No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reorder Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reagents.map((reagent) => (
              <tr key={reagent.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {reagent.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {reagent.lot_no || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={isLowStock(reagent.quantity, reagent.reorder_level) ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                    {reagent.quantity} {reagent.unit}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {reagent.reorder_level} {reagent.unit}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={isExpiringSoon(reagent.expiry) ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                    {reagent.expiry ? (() => {
                      try {
                        const dateStr = reagent.expiry;
                        const date = dateStr.includes('T') 
                          ? new Date(dateStr)
                          : new Date(dateStr + 'T00:00:00');
                        
                        if (isNaN(date.getTime())) {
                          return dateStr;
                        }
                        return format(date, 'MMM dd, yyyy');
                      } catch (err) {
                        console.error('Date formatting error:', err, reagent.expiry);
                        return reagent.expiry;
                      }
                    })() : '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    {isLowStock(reagent.quantity, reagent.reorder_level) && (
                      <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Low Stock
                      </span>
                    )}
                    {isExpiringSoon(reagent.expiry) && (
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 flex items-center">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Expiring
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                  <button
                    onClick={() => viewUsageHistory(reagent)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Package className="w-4 h-4 inline" />
                  </button>
                  <button
                    onClick={() => handleEdit(reagent)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Edit2 className="w-4 h-4 inline" />
                  </button>
                  <button
                    onClick={() => handleDelete(reagent.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingReagent ? 'Edit Reagent' : 'Add Reagent'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lot Number</label>
                <input
                  type="text"
                  value={formData.lot_no}
                  onChange={(e) => setFormData({ ...formData, lot_no: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="ml">ml</option>
                    <option value="L">L</option>
                    <option value="tests">tests</option>
                    <option value="strips">strips</option>
                    <option value="units">units</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData({ ...formData, reorder_level: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiry}
                  onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingReagent(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingReagent ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUsageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Usage History</h2>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usageHistory.map((usage) => (
                  <tr key={usage.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {new Date(usage.used_on).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {usage.quantity_used} {usage.reagent?.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {usage.test_id || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowUsageModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReagentInventory;
